import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  CircularProgress,
  Alert,
  Tabs,
  Tab,
  Box,
  Typography,
  IconButton,
  Divider,
  InputAdornment
} from '@mui/material';
import {
  Close as CloseIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  QrCode as WechatIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { authAPI } from '../../services/api';
import { useUser } from '../../contexts/UserContext';
import { extractErrorMessage, extractFieldError } from '../../utils/errorHandler.js';

// 验证函数
function validateEmail(email) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email);
}


function validatePhone(phone) {
  return /^1[3-9]\d{9}$/.test(phone);
}

function validateSmsCode(code) {
  return /^\d{6}$/.test(code);
}

const MultiLoginDialog = ({ open, onClose, onLoginSuccess }) => {
  // 使用用户上下文
  const { login: userLogin } = useUser();
  
  // 登录方式状态
  const [loginType, setLoginType] = useState(0); // 0: 邮箱验证码, 1: 微信扫码 (手机验证码已屏蔽)
  
  // 邮箱验证码登录状态
  const [email, setEmail] = useState('');
  const [emailCode, setEmailCode] = useState('');
  const [emailError, setEmailError] = useState('');
  const [emailCodeError, setEmailCodeError] = useState('');
  const [emailCountdown, setEmailCountdown] = useState(0);
  const [emailLoading, setEmailLoading] = useState(false);
  
  // 手机验证码登录状态
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [smsCodeError, setSmsCodeError] = useState('');
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [smsLoading, setSmsLoading] = useState(false);
  
  // 微信扫码登录状态
  const [qrLoading, setQrLoading] = useState(false);
  const [qrStatus, setQrStatus] = useState('waiting'); // waiting, scanned, success, expired
  
  // 通用状态
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  // 组件挂载时获取微信二维码
  useEffect(() => {
    if (open && loginType === 1) { // 微信扫码现在是索引1
      // 检查微信SDK是否加载
      if (window.WxLogin) {
        getWechatQRCode();
      } else {
        setError('微信登录SDK未加载，请刷新页面重试');
      }
    }
    // 微信SDK会自动清理，无需手动清理
  }, [open, loginType]);

  // 处理登录成功
  const handleLoginSuccess = async (userInfo, token, expiresIn) => {
    // 保存用户登录状态
    const loginSuccess = await userLogin(userInfo, token, expiresIn);
    
    if (loginSuccess) {
      setSuccess(true);
      setError(null);
      
      // 延迟关闭对话框，让用户看到成功消息
      setTimeout(() => {
        onClose();
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }, 1000);
    } else {
      setError('登录状态保存失败，请重试');
    }
  };

  // 监听微信登录成功消息
  useEffect(() => {
    const handleMessage = (event) => {
      // 检查消息来源和类型
      if (event.data && event.data.type === 'WECHAT_LOGIN_SUCCESS') {
        const { user_info, token, expires_in } = event.data.data;
        handleLoginSuccess(user_info, token, expires_in);
      } else if (event.data && event.data.type === 'WECHAT_LOGIN_CLOSE') {
        // 可以在这里添加关闭逻辑
      }
    };
    
    // 监听来自iframe的消息
    window.addEventListener('message', handleMessage);
    
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // 获取微信登录配置并初始化二维码
  const getWechatQRCode = async () => {
    setQrLoading(true);
    setQrStatus('waiting');
    setError(null); // 清除之前的错误
    
    try {
      const response = await authAPI.getWechatQRCode();
      
      if (response.success && response.data.app_id) {
        // 等待DOM元素渲染完成
        setTimeout(() => {
          // 清空二维码容器
          const container = document.getElementById('wechat-qr-container');
          
          if (container) {
            container.innerHTML = '';
            
            // 使用微信官方JS SDK生成二维码
            if (window.WxLogin) {
              try {
                const wxLogin = new window.WxLogin({
                  self_redirect: true, // 设置为true，让微信在iframe内跳转
                  id: "wechat-qr-container", 
                  appid: response.data.app_id,
                  scope: response.data.scope,
                  redirect_uri: encodeURIComponent(response.data.redirect_uri),
                  state: response.data.state,
                  style: "black",
                  href: ""
                });
                
                // 微信SDK会自动处理状态更新，无需手动轮询
              } catch (wxError) {
                setError('微信登录初始化失败: ' + wxError.message);
              }
            } else {
              setError('微信登录SDK未加载，请刷新页面重试');
            }
          } else {
            setError('页面元素未找到，请刷新页面重试');
          }
        }, 200); // 增加延迟时间到200ms
      } else {
        setError('获取微信登录配置失败: ' + (response.message || '未知错误'));
      }
    } catch (err) {
      setError('获取微信登录配置失败: ' + (err.message || '网络错误'));
    } finally {
      setQrLoading(false);
    }
  };

  // 处理微信登录回调
  const handleWechatCallback = async (code, state) => {
    setQrStatus('success');
    
    try {
      // 调用后端处理微信登录
      const response = await fetch('/api/auth/wechat/callback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ code, state })
      });
      
      const data = await response.json();
      
      if (data.success && data.data?.token) {
        handleLoginSuccess(data.data.user_info, data.data.token, data.data.expires_in);
      } else {
        setError('微信登录失败: ' + (data.message || '未知错误'));
      }
    } catch (err) {
      setError('微信登录失败: ' + err.message);
    }
  };

  // 注意：使用微信JS SDK后，不需要手动轮询状态
  // 微信SDK会自动处理二维码生成、状态更新和回调

  // 发送邮箱验证码
  const sendEmailCode = async () => {
    if (!validateEmail(email)) {
      setEmailError('请输入合法的邮箱地址');
      return;
    }
    
    setEmailError('');
    setEmailLoading(true);
    
    try {
      const response = await authAPI.sendEmailCode(email);
      if (response.success) {
        setEmailCountdown(60);
        const countdown = setInterval(() => {
          setEmailCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdown);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.message || '发送验证码失败');
      }
    } catch (err) {
      // 使用统一的错误处理
      const errorMessage = extractErrorMessage(err, '发送验证码失败');
      const emailFieldError = extractFieldError(err, 'email');
      
      if (emailFieldError) {
        setEmailError(emailFieldError);
      }
      
      setError(errorMessage);
    } finally {
      setEmailLoading(false);
    }
  };

  // 发送短信验证码
  const sendSmsCode = async () => {
    if (!validatePhone(phone)) {
      setPhoneError('请输入正确的手机号');
      return;
    }
    
    setPhoneError('');
    setSmsLoading(true);
    
    try {
      const response = await authAPI.sendSms(phone);
      if (response.success) {
        setSmsCountdown(60);
        const countdown = setInterval(() => {
          setSmsCountdown(prev => {
            if (prev <= 1) {
              clearInterval(countdown);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(response.message || '发送验证码失败');
      }
    } catch (err) {
      // 使用统一的错误处理
      const errorMessage = extractErrorMessage(err, '发送验证码失败');
      const phoneFieldError = extractFieldError(err, 'phone');
      
      if (phoneFieldError) {
        setPhoneError(phoneFieldError);
      }
      
      setError(errorMessage);
    } finally {
      setSmsLoading(false);
    }
  };

  // 邮箱验证码登录
  const handleEmailLogin = async () => {
    setError(null);
    setSuccess(false);
    
    // 验证邮箱
    if (!validateEmail(email)) {
      setEmailError('请输入合法的邮箱地址');
      return;
    } else {
      setEmailError('');
    }
    
    // 验证验证码
    if (!validateSmsCode(emailCode)) {
      setEmailCodeError('请输入6位数字验证码');
      return;
    } else {
      setEmailCodeError('');
    }
    
    setLoading(true);
    try {
      const response = await authAPI.emailLogin({ 
        email, 
        emailCode,
        loginType: 'email'
      });
      
      if (response.success && response.data?.token) {
        handleLoginSuccess(response.data.user_info, response.data.token, response.data.expires_in);
      } else {
        throw new Error(response.message || '登录失败');
      }
    } catch (err) {
      // 使用统一的错误处理
      const errorMessage = extractErrorMessage(err, '登录失败');
      const emailCodeFieldError = extractFieldError(err, 'emailCode');
      const emailFieldError = extractFieldError(err, 'email');
      
      if (emailCodeFieldError) {
        setEmailCodeError(emailCodeFieldError);
      }
      if (emailFieldError) {
        setEmailError(emailFieldError);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // 手机验证码登录
  const handleSmsLogin = async () => {
    setError(null);
    setSuccess(false);
    
    // 验证手机号
    if (!validatePhone(phone)) {
      setPhoneError('请输入正确的手机号');
      return;
    } else {
      setPhoneError('');
    }
    
    // 验证验证码
    if (!validateSmsCode(smsCode)) {
      setSmsCodeError('请输入6位数字验证码');
      return;
    } else {
      setSmsCodeError('');
    }
    
    setLoading(true);
    try {
      const response = await authAPI.smsLogin({ 
        phone, 
        smsCode,
        loginType: 'sms'
      });
      
      if (response.success && response.data?.token) {
        handleLoginSuccess(response.data.user_info, response.data.token, response.data.expires_in);
      } else {
        throw new Error(response.message || '登录失败');
      }
    } catch (err) {
      // 使用统一的错误处理
      const errorMessage = extractErrorMessage(err, '登录失败');
      const smsCodeFieldError = extractFieldError(err, 'smsCode');
      const phoneFieldError = extractFieldError(err, 'phone');
      
      if (smsCodeFieldError) {
        setSmsCodeError(smsCodeFieldError);
      }
      if (phoneFieldError) {
        setPhoneError(phoneFieldError);
      }
      
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };


  // 关闭弹窗
  const handleClose = () => {
    // 清理状态
    setEmail('');
    setEmailCode('');
    setPhone('');
    setSmsCode('');
    setEmailError('');
    setEmailCodeError('');
    setPhoneError('');
    setSmsCodeError('');
    setError(null);
    setSuccess(false);
    setQrStatus('waiting');
    
    onClose();
  };

  // 切换登录方式
  const handleTabChange = (event, newValue) => {
    setLoginType(newValue);
    setError(null);
    setSuccess(false);
    
    // 如果切换到微信登录，获取二维码
    if (newValue === 1) { // 微信扫码现在是索引1
      getWechatQRCode();
    }
  };

  // 渲染邮箱验证码登录
  const renderEmailLogin = () => (
    <Box>
      <TextField
        label="邮箱"
        type="email"
        fullWidth
        margin="normal"
        value={email}
        onChange={e => setEmail(e.target.value)}
        disabled={loading || success}
        error={!!emailError}
        helperText={emailError}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <EmailIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <TextField
          label="验证码"
          fullWidth
          value={emailCode}
          onChange={e => setEmailCode(e.target.value)}
          disabled={loading || success}
          error={!!emailCodeError}
          helperText={emailCodeError}
        />
        <Button
          variant="outlined"
          onClick={sendEmailCode}
          disabled={emailLoading || emailCountdown > 0 || !email || loading || success}
          sx={{ minWidth: 120, height: 56, whiteSpace: 'nowrap' }}
        >
          {emailLoading ? (
            <CircularProgress size={20} />
          ) : emailCountdown > 0 ? (
            `${emailCountdown}s`
          ) : (
            '发送验证码'
          )}
        </Button>
      </Box>
    </Box>
  );

  // 渲染手机验证码登录
  const renderSmsLogin = () => (
    <Box>
      <TextField
        label="手机号"
        fullWidth
        margin="normal"
        value={phone}
        onChange={e => setPhone(e.target.value)}
        disabled={loading || success}
        error={!!phoneError}
        helperText={phoneError}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <PhoneIcon color="action" />
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
        <TextField
          label="验证码"
          fullWidth
          value={smsCode}
          onChange={e => setSmsCode(e.target.value)}
          disabled={loading || success}
          error={!!smsCodeError}
          helperText={smsCodeError}
        />
        <Button
          variant="outlined"
          onClick={sendSmsCode}
          disabled={smsLoading || smsCountdown > 0 || !phone || loading || success}
          sx={{ minWidth: 120, height: 56, whiteSpace: 'nowrap' }}
        >
          {smsLoading ? (
            <CircularProgress size={20} />
          ) : smsCountdown > 0 ? (
            `${smsCountdown}s`
          ) : (
            '发送验证码'
          )}
        </Button>
      </Box>
    </Box>
  );

  // 渲染微信扫码登录
  const renderWechatLogin = () => (
    <Box sx={{ textAlign: 'center', py: 2 }}>
      {qrLoading ? (
        <Box sx={{ py: 4 }}>
          <CircularProgress size={40} />
          <Typography variant="body2" sx={{ mt: 2 }}>正在生成二维码...</Typography>
        </Box>
      ) : (
        <Box>
          {/* 微信二维码容器 */}
          <div 
            id="wechat-qr-container" 
            style={{ 
              minHeight: '200px', 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              border: '1px solid #e0e0e0',
              borderRadius: 8,
              margin: '0 auto',
              maxWidth: '300px',
              backgroundColor: '#fafafa'
            }}
          >
            <Typography variant="body2" color="text.secondary">
              正在生成二维码...
            </Typography>
          </div>
          
          <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
            {qrStatus === 'waiting' && '请使用微信扫描二维码登录'}
            {qrStatus === 'scanned' && '请在手机上确认登录'}
            {qrStatus === 'success' && '登录成功！'}
            {qrStatus === 'expired' && '二维码已过期，请重新生成'}
          </Typography>
          
          {/* 只在二维码过期或出错时显示刷新按钮 */}
          {(qrStatus === 'expired' || error) && (
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={getWechatQRCode}
              sx={{ mt: 2 }}
              disabled={qrLoading}
            >
              重新生成二维码
            </Button>
          )}
        </Box>
      )}
    </Box>
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">登录</Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ px: 3, py: 2 }}>
        <Tabs
          value={loginType}
          onChange={handleTabChange}
          variant="fullWidth"
          sx={{ mb: 2 }}
        >
          <Tab 
            label="邮箱验证码" 
            icon={<EmailIcon />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
          {/* 手机验证码登录已屏蔽 */}
          {/* <Tab 
            label="手机验证码" 
            icon={<PhoneIcon />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          /> */}
          <Tab 
            label="微信扫码" 
            icon={<WechatIcon />} 
            iconPosition="start"
            sx={{ textTransform: 'none' }}
          />
        </Tabs>
        
        <Divider sx={{ mb: 2 }} />
        
        {loginType === 0 && renderEmailLogin()}
        {/* 手机验证码登录已屏蔽 */}
        {/* {loginType === 1 && renderSmsLogin()} */}
        {loginType === 1 && renderWechatLogin()} {/* 微信扫码现在是索引1 */}
        
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            登录成功，正在进入系统...
          </Alert>
        )}
        
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading || success}>
          取消
        </Button>
        {loginType === 0 && (
          <Button
            onClick={handleEmailLogin}
            variant="contained"
            disabled={loading || !email || !emailCode || success}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? '登录中...' : '登录'}
          </Button>
        )}
        {/* 手机验证码登录按钮已屏蔽 */}
        {/* {loginType === 1 && (
          <Button
            onClick={handleSmsLogin}
            variant="contained"
            disabled={loading || !phone || !smsCode || success}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            {loading ? '登录中...' : '登录'}
          </Button>
        )} */}
        {loginType === 1 && ( /* 微信扫码现在是索引1 */
          <Button
            onClick={getWechatQRCode}
            variant="outlined"
            startIcon={<RefreshIcon />}
            disabled={qrLoading}
          >
            {qrLoading ? '生成中...' : '刷新二维码'}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default MultiLoginDialog;
